import { type ReactNode, memo } from 'react';
import { Button, Stack, Text, Title } from '@mantine/core';
import {
  IconAlertTriangle,
  IconClockOff,
  IconMailOff,
  IconUserCheck,
  IconWifiOff,
  IconX,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import type { InvitationErrorType } from '@/hooks/useInvitationFlow';
import styles from '../InvitationPage.module.css';

interface InvitationErrorStateProps {
  errorType: InvitationErrorType;
  onRetry: () => void;
  onLogout?: () => void;
}

const ERROR_ICON_SIZE = 48;

const ERROR_ICON_MAP: Record<InvitationErrorType, ReactNode> = {
  not_found: <IconAlertTriangle size={ERROR_ICON_SIZE} />,
  expired: <IconClockOff size={ERROR_ICON_SIZE} />,
  already_accepted: <IconUserCheck size={ERROR_ICON_SIZE} />,
  revoked: <IconX size={ERROR_ICON_SIZE} />,
  email_mismatch: <IconMailOff size={ERROR_ICON_SIZE} />,
  already_member: <IconUserCheck size={ERROR_ICON_SIZE} />,
  network_error: <IconWifiOff size={ERROR_ICON_SIZE} />,
};

const ERROR_TRANSLATION_MAP: Record<
  InvitationErrorType,
  { title: string; description: string }
> = {
  not_found: {
    title: 'invitation.error.notFound',
    description: 'invitation.error.notFoundDescription',
  },
  expired: {
    title: 'invitation.error.expired',
    description: 'invitation.error.expiredDescription',
  },
  already_accepted: {
    title: 'invitation.error.alreadyAccepted',
    description: 'invitation.error.alreadyAcceptedDescription',
  },
  revoked: {
    title: 'invitation.error.revoked',
    description: 'invitation.error.revokedDescription',
  },
  email_mismatch: {
    title: 'invitation.error.emailMismatch',
    description: 'invitation.error.emailMismatchDescription',
  },
  already_member: {
    title: 'invitation.error.alreadyMember',
    description: 'invitation.error.alreadyMemberDescription',
  },
  network_error: {
    title: 'invitation.error.networkError',
    description: 'invitation.error.networkErrorDescription',
  },
};

export const InvitationErrorState = memo(function InvitationErrorState({
  errorType,
  onRetry,
  onLogout,
}: InvitationErrorStateProps): ReactNode {
  const { t } = useTranslation();
  const translationKeys = ERROR_TRANSLATION_MAP[errorType];
  const icon = ERROR_ICON_MAP[errorType];
  const showRetry = errorType === 'network_error';
  const showLogout = errorType === 'email_mismatch' && onLogout !== undefined;

  return (
    <div className={styles.errorWrapper}>
      <div className={styles.errorIcon}>{icon}</div>
      <Stack align="center" gap="xs">
        <Title order={3} ta="center">
          {t(translationKeys.title)}
        </Title>
        <Text c="dimmed" ta="center" size="sm">
          {t(translationKeys.description)}
        </Text>
        {showRetry && (
          <Button variant="outline" onClick={onRetry}>
            {t('invitation.error.retry')}
          </Button>
        )}
        {showLogout && (
          <Button variant="outline" onClick={onLogout}>
            {t('invitation.error.logout')}
          </Button>
        )}
      </Stack>
    </div>
  );
});
